import { useCallback, useEffect, useRef, useState } from "react";

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Seçim drawer'ları için sunucuda sayfalı arama: gecikmeli (debounce) arama,
 * liste sonuna yaklaşınca sonraki sayfa, eski aramanın geç gelen cevabını
 * yok sayma. Kaydırma kabına `scrollRef` ve `onScroll` bağlanır.
 *
 * @param {boolean} open - Drawer açık mı; açılınca arama sıfırlanır
 * @param {({ query: string, page: number }) => Promise<{ items: Array<{key: string}>, hasMore: boolean }>} fetchPage
 */
export default function usePagedSearch(open, fetchPage) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(false);

  const requestIdRef = useRef(0);
  const fetchingRef = useRef(false);
  // Callback ref → state: Drawer içeriği portal ile sonradan mount olur.
  const [scrollEl, scrollRef] = useState(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setDebouncedQuery("");
    }
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const loadPage = useCallback(
    async (q, nextPage) => {
      if (!fetchPage) return;
      const reqId = ++requestIdRef.current;
      fetchingRef.current = true;
      setFetching(true);
      setError(false);
      try {
        const res = await fetchPage({ query: q, page: nextPage });
        if (reqId !== requestIdRef.current) return;
        const incoming = res?.items || [];
        setItems((prev) => {
          if (nextPage === 1) return incoming;
          const seen = new Set(prev.map((o) => o.key));
          return [...prev, ...incoming.filter((o) => !seen.has(o.key))];
        });
        setPage(nextPage);
        setHasMore(Boolean(res?.hasMore) && incoming.length > 0);
      } catch (_) {
        if (reqId !== requestIdRef.current) return;
        setError(true);
        setHasMore(false);
      } finally {
        if (reqId === requestIdRef.current) {
          fetchingRef.current = false;
          setFetching(false);
        }
      }
    },
    [fetchPage]
  );

  // Açılışta ve arama değişince baştan yükle
  useEffect(() => {
    if (!open) return;
    setItems([]);
    setPage(0);
    setHasMore(true);
    if (scrollEl) scrollEl.scrollTop = 0;
    loadPage(debouncedQuery, 1);
    // scrollEl yalnız başa sarmak için; değişince yeniden yükleme istemiyoruz
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debouncedQuery, loadPage]);

  // Liste sonuna 200px kala sonraki sayfa. (Drawer içinde IntersectionObserver
  // güvenilir tetiklenmedi; kaydırma olayı deterministik.)
  const onScroll = (e) => {
    const el = e.currentTarget;
    if (!hasMore || fetchingRef.current || page === 0) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
      loadPage(debouncedQuery, page + 1);
    }
  };

  return {
    query,
    setQuery,
    debouncedQuery,
    items,
    fetching,
    error,
    hasMore,
    searchPending: query.trim() !== debouncedQuery || (fetching && page === 0),
    initialLoading: fetching && items.length === 0,
    retry: () => loadPage(debouncedQuery, page + 1),
    scrollRef,
    onScroll,
  };
}
